import { useMemo } from 'react';
import { Button } from 'antd';
import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';

export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

/**
 * A cursor-paged admin list, loaded page by page until the admin stops asking.
 * Replaces "fetch the first 100 and hope": a queue is never cut off at an
 * arbitrary size. Rows repeated across pages (new rows arriving while paging)
 * are dropped.
 */
export function useCursorPages<T extends { id: string }>(
  queryKey: QueryKey,
  fetchPage: (cursor: string | undefined) => Promise<CursorPage<T>>,
  options: { refetchInterval?: number } = {},
) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: options.refetchInterval,
  });
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const row of page.items) {
        if (seen.has(row.id)) continue;
        seen.add(row.id);
        out.push(row);
      }
    }
    return out;
  }, [query.data]);
  return {
    rows,
    isLoading: query.isLoading,
    /** The FIRST page failed: nothing is shown, say so (don't render an empty table). */
    isError: query.isError && !query.data,
    error: query.error,
    refetch: () => void query.refetch(),
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}

/** A bare array of `limit + 1` rows (the older list shape) → a cursor page. */
export function pageFromOverfetch<T extends { id: string }>(rows: T[], limit: number): CursorPage<T> {
  const items = rows.slice(0, limit);
  return { items, nextCursor: rows.length > limit ? (items[items.length - 1]?.id ?? null) : null };
}

/** "Load more" under a table; renders nothing when everything is loaded. */
export function LoadMore({
  list,
  noun,
}: {
  list: { rows: unknown[]; hasMore: boolean; loadingMore: boolean; loadMore: () => void };
  noun: string;
}) {
  if (!list.hasMore) return null;
  return (
    <div style={{ textAlign: 'center', marginTop: 12 }}>
      <Button loading={list.loadingMore} onClick={list.loadMore}>
        Load more {noun} ({list.rows.length.toLocaleString('en-IN')} loaded)
      </Button>
    </div>
  );
}
